import {
  connectSocial,
  getBrand,
  getSocials,
  removeSocialMediaAccount,
  axiosInstance,
} from "api";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { platformNameMap, showAlert } from "utils/utils";

const useManageChannels = (brandId) => {
  const queryClient = useQueryClient();

  const [selectedChannel, setSelectedChannel] = useState();
  const [connecting, setConnecting] = useState(false);
  const [showRefreshConnectionModal, setShowRefreshConnectionModal] =
    useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [openOptionId, setOpenOptionId] = useState("");

  //Fetch brand details
  const {
    data: brand,
    isLoading: brandLoading,
    refetch: refetchBrand,
  } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: async () => {
      const res = await getBrand(brandId);
      if (res?.data?.status) return res.data.data;
      throw new Error("Failed to fetch brand details");
    },
    select: (data) => data || {},
    enabled: !!brandId,
    staleTime: 1000 * 60 * 5,
  });

  //Fetch socials
  const {
    data: socials,
    isLoading: socialsLoading,
    refetch: refetchSocials,
  } = useQuery({
    queryKey: ["socials", brandId],
    queryFn: async () => {
      const { search } = window.location;
      const error = new URLSearchParams(search).get("error");
      const connected_platform = new URLSearchParams(search).get("platform");
      const connected_from_post = new URLSearchParams(search).get(
        "connected_from_post"
      );

      const res = await getSocials(brandId);
      if (!res?.data?.status) throw new Error("Failed to fetch socials");

      if (!connected_from_post) {
        if (connected_platform === "Youtube") setShowYoutubeModal(true);
        else if (error) {
          showAlert({ msg: error, type: "danger" });
        }
      }

      return res.data.data.socials || [];
    },
    select: (data) => data || [],
    enabled: !!brandId,
    staleTime: 1000 * 60 * 3,
  });
  
  const loading = brandLoading || socialsLoading;

  const platformList = useMemo(
    () => socials?.map((item) => item.platform) || [],
    [socials]
  );
  

  const removeSocialMutation = useMutation({
    mutationFn: async (channel) => {
      const { platform, id } = channel;
      const res = await removeSocialMediaAccount({ id, brandId, platform });
      if (!res.data.status) throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: (_, channel) => {
      queryClient.invalidateQueries(["socials", brandId]);
      showAlert({
        type: "success",
        msg: `${platformNameMap[channel.platform]} account removed`,
      });
    },
    onError: (error) => {
      showAlert({
        type: "danger",
        msg: error?.response?.data?.message || "Error removing platform",
      });
    },
  });

  const handleRemoveChannel = (channel) => {
    setSelectedChannel(channel);
    toggleShowOptions("");
    setTimeout(() =>
      document.querySelector("#confirmPlatformRemoval").classList.add("show")
    );
  };

  const handleConfirmRemove = () => {
    if (!selectedChannel) {
      return;
    }
    removeSocialMutation.mutate(selectedChannel);
  };

  const toggleShowOptions = (id) => {
    setOpenOptionId((prevId) => (prevId === id ? "" : id));
  };

  const handleTokenRefresh = async (channel) => {
    const { platform } = channel;
    if (platform === "youtube") return;

    try {
      const res = await axiosInstance.get(`${platform}/${brandId}/refresh`);

      if (res?.data?.status) {
        const redirectUrl = res.data?.redirect;
        if (redirectUrl) return window.location.replace(redirectUrl);
        if (channel.platform === "tiktok") window.location.reload();

        showAlert({ msg: res.data?.message, type: "success" });
      } else {
        showAlert({ msg: res.data?.message, type: "danger" });
      }
    } catch (e) {
      showAlert({
        msg:
          e.response?.data?.message ||
          "An error occurred. Could not refresh connection.",
        type: "danger",
      });
    } finally {
      refetchSocials();
    }
  };

  const handleConnectPlatform = async (platform, direct = false) => {
    setConnecting(true);
    if (!platform) {
      setConnecting(false);
      return;
    }

    try {
      const res = await connectSocial(platform, direct);
      if (res?.data?.status) {
        const redirectUrl = res.data.redirect;
        if (redirectUrl) window.location.replace(redirectUrl);
      } else {
        showAlert({ msg: res.data?.message, type: "danger" });
      }
    } catch (e) {
      showAlert({
        msg:
          e.response?.data?.message ||
          "An error occurred. Could not complete the request.",
        type: "danger",
      });
    } finally {
      setConnecting(false);
    }
  };

  return {
    brand,
    socials,
    platformList,
    loading,
    connecting,
    showRefreshConnectionModal,
    setShowRefreshConnectionModal,
    showYoutubeModal,
    setShowYoutubeModal,
    selectedChannel,
    setSelectedChannel,
    refetchBrand,
    loadSocials: refetchSocials,
    refetchSocials,
    handleRemoveChannel,
    toggleShowOptions,
    openOptionId,
    handleTokenRefresh,
    handleConnectPlatform,
    removeSocial: handleConfirmRemove,
  };
};

export default useManageChannels;
